import type { ApiJob, JobStatusName } from "@tradingdroid/frontends-types";

import { getApiErrorMessage } from "@src/utils/apiError";

const PAUSABLE_STATUSES: readonly JobStatusName[] = ["queued", "claimed", "running"];
const RESUMABLE_STATUSES: readonly JobStatusName[] = ["paused", "cancelled"];

// A coordinator drives these children itself and would neither wait for nor resume one.
const COORDINATOR_CHILD_ORIGINS = new Set([
  "walk_forward_fold",
  "walk_forward_cpcv_child",
  "monte_carlo_child",
]);

/** Only a standalone optimization honours a pause and continues its study on resume. */
const supportsPauseResume = (job: ApiJob): boolean =>
  job.job_type_name === "optimization" &&
  !COORDINATOR_CHILD_ORIGINS.has(job.metadata?.origin?.type ?? "");

export const canPauseJob = (job: ApiJob): boolean =>
  supportsPauseResume(job) && PAUSABLE_STATUSES.includes(job.status_name);

export const canResumeJob = (job: ApiJob): boolean =>
  supportsPauseResume(job) && RESUMABLE_STATUSES.includes(job.status_name);

const STOPPED_STATUSES: readonly JobStatusName[] = ["paused", "cancelled"];

// Whole seconds since the epoch, the precision the API renders claim_ts at; naive values are UTC.
const toEpochSecond = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const trimmed = value.replace(/\.\d+/, "");
  const ms = Date.parse(/(Z|[+-]\d{2}:?\d{2})$/.test(trimmed) ? trimmed : `${trimmed}Z`);
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
};

/**
 * Whether the worker that held a paused or cancelled optimization has written its stop acknowledgement
 * for the current claim. Until it has, the result still holds what an earlier run left there.
 */
export const isStopAcknowledged = (job: ApiJob): boolean => {
  if (job.job_type_name !== "optimization" || !STOPPED_STATUSES.includes(job.status_name)) {
    return false;
  }
  // Never claimed, or the claim was reset: no worker is left to acknowledge anything.
  if (!job.worker_id) return true;
  const ack = job.result?.data?.stopped_ack;
  return (
    ack?.worker_id === job.worker_id &&
    ack.status === job.status_name &&
    toEpochSecond(ack.claim_ts) === toEpochSecond(job.claim_ts)
  );
};

/**
 * Trials a paused optimization has finished. Read from the result once the worker has acknowledged
 * the stop, and from the live progress before that, when the result is still an earlier run's.
 */
export const getPausedTrials = (job: ApiJob): number | undefined => {
  if (job.job_type_name !== "optimization" || job.status_name !== "paused") return undefined;
  const fromResult = job.result?.data?.n_trials;
  if (isStopAcknowledged(job) && typeof fromResult === "number") return fromResult;
  const fromState = Number.parseInt(job.running_state ?? "", 10);
  return Number.isNaN(fromState) ? undefined : fromState;
};

const httpStatusOf = (error: unknown): number | undefined =>
  typeof error === "object" && error !== null && "response" in error
    ? (error as { response?: { status?: number } }).response?.status
    : undefined;

/** The 409 a resume answers while the worker that held the job has not acknowledged its stop. */
export const isWorkerStillStoppingError = (error: unknown): boolean =>
  httpStatusOf(error) === 409 && getApiErrorMessage(error).includes("has not stopped yet");
